import { useState, useCallback, useMemo, useEffect } from 'react';
import { Inventory, Label, Layers, People, CreditCard, Palette } from '@mui/icons-material';
import {
  SystemParameterCategories,
  SystemParameterItem,
} from '../../../types/system-parameters.types';
import { CategoriesService } from '../../../api/services/CategoriesService';
import { TagsService } from '../../../api/services/TagsService';
import { MaterialsService } from '../../../api/services/MaterialsService';
import { ColorsService } from '../../../api/services/ColorsService';
import { SuppliersService } from '../../../api/services/SuppliersService';
import { PaymentMethodsService } from '../../../api/services/PaymentMethodsService';
import { Category } from '../../../api/models/Category';
import { Tag } from '../../../api/models/Tag';
import { Material } from '../../../api/models/Material';
import { Color } from '../../../api/models/Color';
import { Supplier } from '../../../api/models/Supplier';
import { PaymentMethod } from '../../../api/models/PaymentMethod';
import { CreateCategoryDto } from '../../../api/models/CreateCategoryDto';
import { UpdateCategoryDto } from '../../../api/models/UpdateCategoryDto';
import { CreateTagDto } from '../../../api/models/CreateTagDto';
import { UpdateTagDto } from '../../../api/models/UpdateTagDto';
import { CreateMaterialDto } from '../../../api/models/CreateMaterialDto';
import { UpdateMaterialDto } from '../../../api/models/UpdateMaterialDto';
import { CreateColorDto } from '../../../api/models/CreateColorDto';
import { UpdateColorDto } from '../../../api/models/UpdateColorDto';
import { CreateSupplierDto } from '../../../api/models/CreateSupplierDto';
import { UpdateSupplierDto } from '../../../api/models/UpdateSupplierDto';
import { CreatePaymentMethodDto } from '../../../api/models/CreatePaymentMethodDto';
import { UpdatePaymentMethodDto } from '../../../api/models/UpdatePaymentMethodDto';
import { splitPhoneValue, validatePhoneByCountry } from '../../../utils/phone';

// Utility function to translate error messages to Spanish
const translateErrorMessage = (message: string): string => {
  // Traducciones completas de mensajes del backend (ordenadas de más específico a menos específico)
  const translations: { [key: string]: string } = {
    // Mensajes completos de eliminación (más específicos primero)
    'Cannot delete category - it is being used by products':
      'No se puede eliminar la categoría porque está siendo utilizada por productos',
    'Cannot delete tag - it is being used by products':
      'No se puede eliminar la etiqueta porque está siendo utilizada por productos',
    'Cannot delete material - it is being used by products':
      'No se puede eliminar el material porque está siendo utilizada por productos',
    'Cannot delete supplier - it is being used by products':
      'No se puede eliminar el proveedor porque está siendo utilizada por productos',
    'Cannot delete payment method': 'No se puede eliminar el método de pago',
    'Cannot delete payment method - it is being used by':
      'No se puede eliminar el método de pago porque está siendo utilizado por',

    // Mensajes parciales de eliminación (fallback)
    'Cannot delete category': 'No se puede eliminar la categoría',
    'Cannot delete tag': 'No se puede eliminar la etiqueta',
    'Cannot delete material': 'No se puede eliminar el material',
    'Cannot delete supplier': 'No se puede eliminar el proveedor',

    // Mensajes de duplicados (completos primero)
    'Category with name': 'Categoría con nombre',
    'Tag with name': 'Etiqueta con nombre',
    'Material with name': 'Material con nombre',
    'Color with name': 'Color con nombre',
    'Supplier with name': 'Proveedor con nombre',
    'already exists': 'ya existe',
    'Tag name already exists': 'El nombre de la etiqueta ya existe',
    'Category name already exists': 'El nombre de la categoría ya existe',
    'Category code already exists': 'El código de la categoría ya existe',
    'Material name already exists': 'El nombre del material ya existe',
    'Color name already exists': 'El nombre del color ya existe',
    'Supplier name already exists': 'El nombre del proveedor ya existe',
    'Supplier email already exists': 'El email del proveedor ya existe',
    'Payment method with name': 'Método de pago con nombre',
    'Payment method with code': 'Método de pago con código',
    'Payment method already exists': 'El método de pago ya existe',

    // Mensajes de no encontrado
    'Category not found': 'Categoría no encontrada',
    'Tag not found': 'Etiqueta no encontrada',
    'Material not found': 'Material no encontrado',
    'Color not found': 'Color no encontrado',
    'Supplier not found': 'Proveedor no encontrado',
    'Payment method not found': 'Método de pago no encontrado',

    // Mensajes de datos inválidos
    'Invalid category data': 'Datos de categoría inválidos',
    'Invalid tag data': 'Datos de etiqueta inválidos',
    'Invalid material data': 'Datos de material inválidos',
    'Invalid color data': 'Datos de color inválidos',
    'Invalid supplier data': 'Datos de proveedor inválidos',

    // Frases parciales para mensajes compuestos
    'it is being used by': 'está siendo utilizada por',
    'because it is being used by': 'porque está siendo utilizada por',
    'Please reassign or delete the products first': 'Reasigne o elimine los productos primero',
    'Please remove the material from these records first':
      'Elimine el material de estos registros primero',
    'Please remove the tag from these products or reassign first':
      'Elimine la etiqueta de estos productos o reasigne primero',
    'Please remove the tag from these products or delete el almacén inventario primero':
      'Elimine la etiqueta de estos productos o reasigne primero',
    'Please remove': 'Elimine',
    'from these records first': 'de estos registros primero',
    'that have almacén inventario': '', // Eliminar esta frase del mensaje
    'or delete el almacén inventario primero': 'o reasigne primero',
    'or reassign first': 'o reasigne primero',
    'product(s)': 'producto(s)',
    products: 'productos',
    'warehouse inventory item(s)': 'elemento(s) de inventario en almacén',
    'warehouse inventory items': 'elementos de inventario en almacén',
    'inventory item(s)': 'elemento(s) de inventario',
    'inventory items': 'elementos de inventario',
    warehouse: 'almacén',
    inventory: 'inventario',
    'item(s)': 'elemento(s)',
    items: 'elementos',
    'record(s)': 'registro(s)',
    records: 'registros',
    first: 'primero',
    remove: 'eliminar',
    these: 'estos',
    the: 'el',
    from: 'de',

    // Mensajes de autenticación y autorización
    Unauthorized: 'No autorizado',
    Forbidden: 'Acceso denegado',

    // Mensajes de error genéricos
    'Internal server error': 'Error interno del servidor',
    'Network error': 'Error de conexión',
    'Request timeout': 'Tiempo de espera agotado',
    'Bad request': 'Solicitud incorrecta',
  };

  let translatedMessage = message.trim();

  // Si el mensaje está vacío, retornar mensaje genérico
  if (!translatedMessage) {
    return 'Ha ocurrido un error';
  }

  // Manejar mensajes de eliminación de etiqueta con formato detallado
  // Ejemplo del backend: "Cannot delete tag "bestbuy" because it is being used by 2 product(s) that have almacén inventario: iphone 13 (4), iphone 6 (1). Please remove the tag from these products or delete el almacén inventario primero."
  // Formato deseado: "No se puede eliminar la etiqueta "bestbuy" porque está siendo utilizada por 2 producto(s): iphone 13 (4), iphone 6 (1). Elimine la etiqueta de estos productos o reasigne primero."
  const tagDeletePattern =
    /Cannot\s+delete\s+tag\s+"([^"]+)"\s+because\s+it\s+is\s+being\s+used\s+by\s+(\d+)\s+product\(s\)(?:\s+that\s+have\s+[^:]+)?:\s*(.+?)\.\s*(?:Please\s+remove\s+the\s+tag\s+from\s+these\s+products\s+or\s+(?:delete\s+el\s+almacén\s+inventario\s+primero|reassign\s+first)\.)/gi;
  const tagDeleteMatch = translatedMessage.match(tagDeletePattern);
  if (tagDeleteMatch) {
    translatedMessage = translatedMessage.replace(
      tagDeletePattern,
      (match, tagName, productCount, productsList) => {
        // Limpiar espacios extra y puntos finales de la lista de productos
        const cleanProductsList = productsList.trim().replace(/\.$/, '');
        return `No se puede eliminar la etiqueta "${tagName}" porque está siendo utilizada por ${productCount} producto(s): ${cleanProductsList}. Elimine la etiqueta de estos productos o reasigne primero.`;
      },
    );
    return translatedMessage;
  }

  // También manejar el caso donde el mensaje ya está parcialmente traducido pero tiene partes en inglés
  // Ejemplo: "No se puede eliminar la etiqueta "bestbuy" porque está siendo utilizada por 2 producto(s) that have almacén inventario: iphone 13 (4), iphone 6 (1). Elimine el tag de estos productos or delete el almacén inventario primero."
  const partiallyTranslatedPattern =
    /No\s+se\s+puede\s+eliminar\s+la\s+etiqueta\s+"([^"]+)"\s+porque\s+está\s+siendo\s+utilizada\s+por\s+(\d+)\s+producto\(s\)(?:\s+that\s+have\s+[^:]+)?:\s*(.+?)\.\s*(?:Por\s+favor,\s+elimina\s+el\s+tag\s+de\s+estos\s+productos\s+or\s+delete\s+el\s+almacén\s+inventario\s+primero\.)/gi;
  const partiallyTranslatedMatch = translatedMessage.match(partiallyTranslatedPattern);
  if (partiallyTranslatedMatch) {
    translatedMessage = translatedMessage.replace(
      partiallyTranslatedPattern,
      (match, tagName, productCount, productsList) => {
        // Limpiar espacios extra y puntos finales de la lista de productos
        const cleanProductsList = productsList.trim().replace(/\.$/, '');
        return `No se puede eliminar la etiqueta "${tagName}" porque está siendo utilizada por ${productCount} producto(s): ${cleanProductsList}. Elimine la etiqueta de estos productos o reasigne primero.`;
      },
    );
    return translatedMessage;
  }

  // Manejar mensajes con formato "Category with name XXXX already exists"
  // Extraer el nombre y agregarlo con comillas
  const withNamePattern =
    /(Category|Tag|Material|Color|Supplier)\s+with\s+name\s+([^\s]+)\s+already\s+exists/gi;
  const withNameMatch = translatedMessage.match(withNamePattern);
  if (withNameMatch) {
    const entityTranslations: { [key: string]: string } = {
      Category: 'Categoría',
      Tag: 'Etiqueta',
      Material: 'Material',
      Color: 'Color',
      Supplier: 'Proveedor',
    };

    translatedMessage = translatedMessage.replace(withNamePattern, (match, entity, name) => {
      const entitySpanish = entityTranslations[entity] || entity;
      return `${entitySpanish} con nombre "${name}" ya existe`;
    });
  }

  // Aplicar traducciones en orden de especificidad (frases más largas primero)
  const sortedTranslations = Object.entries(translations).sort((a, b) => b[0].length - a[0].length);

  sortedTranslations.forEach(([english, spanish]) => {
    // Escapar caracteres especiales para regex
    const escapedEnglish = english.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Buscar coincidencias exactas (case-insensitive)
    const regex = new RegExp(escapedEnglish, 'gi');
    translatedMessage = translatedMessage.replace(regex, spanish);
  });

  // Si después de todas las traducciones aún hay texto en inglés, intentar traducir palabras sueltas comunes
  // const commonWords: { [key: string]: string } = {
  //   "cannot": "no se puede",
  //   "delete": "eliminar",
  //   "category": "categoría",
  //   "tag": "etiqueta",
  //   "material": "material",
  //   "supplier": "proveedor",
  //   "already": "ya",
  //   "exists": "existe",
  //   "not": "no",
  //   "found": "encontrado",
  //   "invalid": "inválido",
  //   "data": "datos",
  //   "name": "nombre",
  //   "code": "código",
  //   "email": "email",
  //   "error": "error",
  // };

  // Solo aplicar traducción de palabras sueltas si el mensaje aún contiene palabras en inglés comunes
  if (/[a-zA-Z]/.test(translatedMessage) && translatedMessage !== message) {
    // Ya se aplicó alguna traducción, no hacer más cambios
    return translatedMessage;
  }

  return translatedMessage;
};

export interface PaginationState {
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
}

export interface UseSystemParametersReturn {
  // State
  selectedCategory: string;
  searchTerm: string;
  showAddModal: boolean;
  editingItem: SystemParameterItem | null;
  loading: boolean;
  error: string | null;
  showDeleteModal: boolean;
  itemToDelete: SystemParameterItem | null;
  notification: {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  };

  // Data
  categories: SystemParameterCategories;
  currentCategory: SystemParameterCategories[string];
  filteredData: SystemParameterItem[];
  pagination: PaginationState;

  // Handlers
  setSelectedCategory: (category: string) => void;
  setSearchTerm: (term: string) => void;
  setShowAddModal: (show: boolean) => void;
  setEditingItem: (item: SystemParameterItem | null) => void;
  setShowDeleteModal: (show: boolean) => void;
  setItemToDelete: (item: SystemParameterItem | null) => void;
  handleAdd: () => void;
  handleEdit: (item: SystemParameterItem) => void;
  handleDelete: (item: SystemParameterItem) => void;
  handleConfirmDelete: () => void;
  handleSave: (formData: any) => Promise<void>;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePageChange: (page: number) => void;
  handleItemsPerPageChange: (limit: number) => void;
  handleCloseNotification: () => void;
  refreshCategories: () => void;
  refreshTags: () => void;
  refreshMaterials: () => void;
  refreshColors: () => void;
  refreshSuppliers: () => void;
  refreshPaymentMethods: () => void;
}

export const useSystemParameters = (): UseSystemParametersReturn => {
  const [selectedCategory, setSelectedCategory] = useState('tipoProducto');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState<SystemParameterItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categoriesData, setCategoriesData] = useState<Category[]>([]);
  const [tagsData, setTagsData] = useState<Tag[]>([]);
  const [materialsData, setMaterialsData] = useState<Material[]>([]);
  const [colorsData, setColorsData] = useState<Color[]>([]);
  const [suppliersData, setSuppliersData] = useState<Supplier[]>([]);
  const [paymentMethodsData, setPaymentMethodsData] = useState<PaymentMethod[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<SystemParameterItem | null>(null);

  // Notification state
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Pagination state
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 0,
  });

  // API functions
  const fetchCategories = useCallback(
    async (page: number = 1, limit: number = 10, search?: string) => {
      try {
        setLoading(true);
        setError(null);
        const response = await CategoriesService.categoriesControllerFindAll(
          page,
          limit,
          search,
          undefined,
        );

        // Handle the response structure with pagination metadata
        if (response.data && Array.isArray(response.data)) {
          setCategoriesData(response.data);

          // Update pagination with server metadata if available
          if (response.meta) {
            setPagination((prev) => ({
              ...prev,
              totalItems: response.meta.total,
              totalPages: response.meta.totalPages,
              // Don't override the page - use the requested page parameter
              page: page,
              limit: response.meta.limit,
            }));

            // Update category totals
            setCategoryTotals((prev) => ({
              ...prev,
              tipoProducto: response.meta.total,
            }));
          }
        } else {
          console.warn('Unexpected categories response format:', response);
          setCategoriesData([]);
        }
      } catch (err: any) {
        const rawErrorMessage =
          err?.body?.message || err?.message || 'Error al cargar las categorías';
        const errorMessage = translateErrorMessage(rawErrorMessage);
        setError(errorMessage);
        console.error('Error fetching categories:', err);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const createCategory = useCallback(
    async (data: CreateCategoryDto) => {
      try {
        setLoading(true);
        setError(null);
        await CategoriesService.categoriesControllerCreate(data);
        await fetchCategories(pagination.page, pagination.limit, searchTerm); // Refresh with current pagination
      } catch (err: any) {
        const rawErrorMessage = err?.body?.message || err?.message || 'Error al crear la categoría';
        const errorMessage = translateErrorMessage(rawErrorMessage);
        setError(errorMessage);
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        console.error('Error creating category:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchCategories, pagination.page, pagination.limit, searchTerm],
  );

  const updateCategory = useCallback(
    async (id: number, data: UpdateCategoryDto) => {
      try {
        setLoading(true);
        setError(null);
        await CategoriesService.categoriesControllerUpdate(id.toString(), data);
        await fetchCategories(pagination.page, pagination.limit, searchTerm); // Refresh with current pagination
      } catch (err: any) {
        const rawErrorMessage =
          err?.body?.message || err?.message || 'Error al actualizar la categoría';
        const errorMessage = translateErrorMessage(rawErrorMessage);
        setError(errorMessage);
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        console.error('Error updating category:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchCategories, pagination.page, pagination.limit, searchTerm],
  );

  const deleteCategory = useCallback(
    async (id: number) => {
      try {
        setLoading(true);
        setError(null);
        await CategoriesService.categoriesControllerRemove(id.toString());
        await fetchCategories(pagination.page, pagination.limit, searchTerm); // Refresh with current pagination
        setShowDeleteModal(false);
        setItemToDelete(null);
      } catch (err: any) {
        // Extract error message from API response and translate to Spanish
        const rawErrorMessage =
          err?.body?.message || err?.message || 'Error al eliminar la categoría';
        const errorMessage = translateErrorMessage(rawErrorMessage);

        setError(errorMessage);
        // Mostrar notificación en Snackbar
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        console.error('Error deleting category:', err);
        // Cerrar el modal incluso si hay error para mejor UX
        setShowDeleteModal(false);
        setItemToDelete(null);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchCategories, pagination.page, pagination.limit, searchTerm],
  );

  // Tags API functions
  const fetchTags = useCallback(async (page: number = 1, limit: number = 10, search?: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await TagsService.tagsControllerFindAll(page, limit, search);

      // Handle the response structure with pagination metadata
      if (response.data && Array.isArray(response.data)) {
        setTagsData(response.data);

        // Update pagination with server metadata if available
        if (response.meta) {
          setPagination((prev) => ({
            ...prev,
            totalItems: response.meta.total,
            totalPages: response.meta.totalPages,
            // Don't override the page - use the requested page parameter
            page: page,
            limit: response.meta.limit,
          }));

          // Update category totals
          setCategoryTotals((prev) => ({
            ...prev,
            etiquetas: response.meta.total,
          }));
        }
      } else {
        console.warn('Unexpected tags response format:', response);
        setTagsData([]);
      }
    } catch (err: any) {
      const rawErrorMessage = err?.body?.message || err?.message || 'Error al cargar las etiquetas';
      const errorMessage = translateErrorMessage(rawErrorMessage);
      setError(errorMessage);
      console.error('Error fetching tags:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createTag = useCallback(
    async (data: CreateTagDto) => {
      try {
        setLoading(true);
        setError(null);
        await TagsService.tagsControllerCreate(data);
        await fetchTags(pagination.page, pagination.limit, searchTerm); // Refresh with current pagination
      } catch (err: any) {
        const rawErrorMessage = err?.body?.message || err?.message || 'Error al crear la etiqueta';
        const errorMessage = translateErrorMessage(rawErrorMessage);
        setError(errorMessage);
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        console.error('Error creating tag:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchTags, pagination.page, pagination.limit, searchTerm],
  );

  const updateTag = useCallback(
    async (id: number, data: UpdateTagDto) => {
      try {
        setLoading(true);
        setError(null);
        await TagsService.tagsControllerUpdate(id.toString(), data);
        await fetchTags(pagination.page, pagination.limit, searchTerm); // Refresh with current pagination
      } catch (err: any) {
        const rawErrorMessage =
          err?.body?.message || err?.message || 'Error al actualizar la etiqueta';
        const errorMessage = translateErrorMessage(rawErrorMessage);
        setError(errorMessage);
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        console.error('Error updating tag:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchTags, pagination.page, pagination.limit, searchTerm],
  );

  const deleteTag = useCallback(
    async (id: number) => {
      try {
        setLoading(true);
        setError(null);
        await TagsService.tagsControllerRemove(id.toString());
        await fetchTags(pagination.page, pagination.limit, searchTerm); // Refresh with current pagination
        setShowDeleteModal(false);
        setItemToDelete(null);
      } catch (err: any) {
        // Extract error message from API response and translate to Spanish
        const rawErrorMessage =
          err?.body?.message || err?.message || 'Error al eliminar la etiqueta';
        const errorMessage = translateErrorMessage(rawErrorMessage);

        setError(errorMessage);
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        console.error('Error deleting tag:', err);
        // Cerrar el modal incluso si hay error para mejor UX
        setShowDeleteModal(false);
        setItemToDelete(null);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchTags, pagination.page, pagination.limit, searchTerm],
  );

  // Materials API functions
  const fetchMaterials = useCallback(
    async (page: number = 1, limit: number = 10, search?: string) => {
      try {
        setLoading(true);
        setError(null);
        const response = await MaterialsService.materialsControllerFindAll(page, limit, search);

        // Handle the response structure with pagination metadata
        if (response.data && Array.isArray(response.data)) {
          setMaterialsData(response.data);

          // Update pagination with server metadata if available
          if (response.meta) {
            setPagination((prev) => ({
              ...prev,
              totalItems: response.meta.total,
              totalPages: response.meta.totalPages,
              // Don't override the page - use the requested page parameter
              page: page,
              limit: response.meta.limit,
            }));

            // Update category totals
            setCategoryTotals((prev) => ({
              ...prev,
              material: response.meta.total,
            }));
          }
        } else {
          console.warn('Unexpected materials response format:', response);
          setMaterialsData([]);
        }
      } catch (err: any) {
        const rawErrorMessage =
          err?.body?.message || err?.message || 'Error al cargar los materiales';
        const errorMessage = translateErrorMessage(rawErrorMessage);
        setError(errorMessage);
        console.error('Error fetching materials:', err);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const createMaterial = useCallback(
    async (data: CreateMaterialDto) => {
      try {
        setLoading(true);
        setError(null);
        await MaterialsService.materialsControllerCreate(data);
        await fetchMaterials(pagination.page, pagination.limit, searchTerm); // Refresh with current pagination
      } catch (err: any) {
        const rawErrorMessage = err?.body?.message || err?.message || 'Error al crear el material';
        const errorMessage = translateErrorMessage(rawErrorMessage);
        setError(errorMessage);
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        console.error('Error creating material:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchMaterials, pagination.page, pagination.limit, searchTerm],
  );

  const updateMaterial = useCallback(
    async (id: number, data: UpdateMaterialDto) => {
      try {
        setLoading(true);
        setError(null);
        await MaterialsService.materialsControllerUpdate(id.toString(), data);
        await fetchMaterials(pagination.page, pagination.limit, searchTerm); // Refresh with current pagination
      } catch (err: any) {
        const rawErrorMessage =
          err?.body?.message || err?.message || 'Error al actualizar el material';
        const errorMessage = translateErrorMessage(rawErrorMessage);
        setError(errorMessage);
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        console.error('Error updating material:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchMaterials, pagination.page, pagination.limit, searchTerm],
  );

  const deleteMaterial = useCallback(
    async (id: number) => {
      try {
        setLoading(true);
        setError(null);
        await MaterialsService.materialsControllerRemove(id.toString());
        await fetchMaterials(pagination.page, pagination.limit, searchTerm); // Refresh with current pagination
        setShowDeleteModal(false);
        setItemToDelete(null);
      } catch (err: any) {
        // Extract error message from API response and translate to Spanish
        const rawErrorMessage =
          err?.body?.message || err?.message || 'Error al eliminar el material';
        const errorMessage = translateErrorMessage(rawErrorMessage);

        setError(errorMessage);
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        console.error('Error deleting material:', err);
        // Cerrar el modal incluso si hay error para mejor UX
        setShowDeleteModal(false);
        setItemToDelete(null);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchMaterials, pagination.page, pagination.limit, searchTerm],
  );

  // Colors API functions
  const fetchColors = useCallback(async (page: number = 1, limit: number = 10, search?: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await ColorsService.colorsControllerFindAll(page, limit, search);

      if (response.data && Array.isArray(response.data)) {
        setColorsData(response.data);

        if (response.meta) {
          setPagination((prev) => ({
            ...prev,
            totalItems: response.meta.total,
            totalPages: response.meta.totalPages,
            page: page,
            limit: response.meta.limit,
          }));

          setCategoryTotals((prev) => ({
            ...prev,
            color: response.meta.total,
          }));
        }
      } else {
        console.warn('Unexpected colors response format:', response);
        setColorsData([]);
      }
    } catch (err: any) {
      const rawErrorMessage = err?.body?.message || err?.message || 'Error al cargar los colores';
      const errorMessage = translateErrorMessage(rawErrorMessage);
      setError(errorMessage);
      console.error('Error fetching colors:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createColor = useCallback(
    async (data: CreateColorDto) => {
      try {
        setLoading(true);
        setError(null);
        await ColorsService.colorsControllerCreate(data);
        await fetchColors(pagination.page, pagination.limit, searchTerm);
      } catch (err: any) {
        const rawErrorMessage = err?.body?.message || err?.message || 'Error al crear el color';
        const errorMessage = translateErrorMessage(rawErrorMessage);
        setError(errorMessage);
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        console.error('Error creating color:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchColors, pagination.page, pagination.limit, searchTerm],
  );

  const updateColor = useCallback(
    async (id: number, data: UpdateColorDto) => {
      try {
        setLoading(true);
        setError(null);
        await ColorsService.colorsControllerUpdate(id.toString(), data);
        await fetchColors(pagination.page, pagination.limit, searchTerm);
      } catch (err: any) {
        const rawErrorMessage =
          err?.body?.message || err?.message || 'Error al actualizar el color';
        const errorMessage = translateErrorMessage(rawErrorMessage);
        setError(errorMessage);
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        console.error('Error updating color:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchColors, pagination.page, pagination.limit, searchTerm],
  );

  const deleteColor = useCallback(
    async (id: number) => {
      try {
        setLoading(true);
        setError(null);
        await ColorsService.colorsControllerRemove(id.toString());
        await fetchColors(pagination.page, pagination.limit, searchTerm);
        setShowDeleteModal(false);
        setItemToDelete(null);
      } catch (err: any) {
        const rawErrorMessage = err?.body?.message || err?.message || 'Error al eliminar el color';
        const errorMessage = translateErrorMessage(rawErrorMessage);

        setError(errorMessage);
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        console.error('Error deleting color:', err);
        setShowDeleteModal(false);
        setItemToDelete(null);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchColors, pagination.page, pagination.limit, searchTerm],
  );

  // Suppliers API functions
  const fetchSuppliers = useCallback(
    async (page: number = 1, limit: number = 10, search?: string) => {
      try {
        setLoading(true);
        setError(null);
        const response = await SuppliersService.suppliersControllerFindAll(
          page,
          limit,
          search,
          undefined,
          undefined,
        );

        // Handle the response structure with pagination metadata
        if (response.data && Array.isArray(response.data)) {
          setSuppliersData(response.data);

          // Update pagination with server metadata if available
          if (response.meta) {
            setPagination((prev) => ({
              ...prev,
              totalItems: response.meta.total,
              totalPages: response.meta.totalPages,
              // Don't override the page - use the requested page parameter
              page: page,
              limit: response.meta.limit,
            }));

            // Update category totals
            setCategoryTotals((prev) => ({
              ...prev,
              proveedor: response.meta.total,
            }));
          }
        } else {
          console.warn('Unexpected suppliers response format:', response);
          setSuppliersData([]);
        }
      } catch (err: any) {
        const rawErrorMessage =
          err?.body?.message || err?.message || 'Error al cargar los proveedores';
        const errorMessage = translateErrorMessage(rawErrorMessage);
        setError(errorMessage);
        console.error('Error fetching suppliers:', err);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const createSupplier = useCallback(
    async (data: CreateSupplierDto) => {
      try {
        setLoading(true);
        setError(null);
        await SuppliersService.suppliersControllerCreate(data);
        await fetchSuppliers(pagination.page, pagination.limit, searchTerm); // Refresh with current pagination
      } catch (err: any) {
        const rawErrorMessage = err?.body?.message || err?.message || 'Error al crear el proveedor';
        const errorMessage = translateErrorMessage(rawErrorMessage);
        setError(errorMessage);
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        console.error('Error creating supplier:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchSuppliers, pagination.page, pagination.limit, searchTerm],
  );

  const updateSupplier = useCallback(
    async (id: number, data: UpdateSupplierDto) => {
      try {
        setLoading(true);
        setError(null);
        await SuppliersService.suppliersControllerUpdate(id.toString(), data);
        await fetchSuppliers(pagination.page, pagination.limit, searchTerm); // Refresh with current pagination
      } catch (err: any) {
        const rawErrorMessage =
          err?.body?.message || err?.message || 'Error al actualizar el proveedor';
        const errorMessage = translateErrorMessage(rawErrorMessage);
        setError(errorMessage);
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        console.error('Error updating supplier:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchSuppliers, pagination.page, pagination.limit, searchTerm],
  );

  const deleteSupplier = useCallback(
    async (id: number) => {
      try {
        setLoading(true);
        setError(null);
        await SuppliersService.suppliersControllerRemove(id.toString());
        await fetchSuppliers(pagination.page, pagination.limit, searchTerm); // Refresh with current pagination
        setShowDeleteModal(false);
        setItemToDelete(null);
      } catch (err: any) {
        // Extract error message from API response and translate to Spanish
        const rawErrorMessage =
          err?.body?.message || err?.message || 'Error al eliminar el proveedor';
        const errorMessage = translateErrorMessage(rawErrorMessage);

        setError(errorMessage);
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        console.error('Error deleting supplier:', err);
        // Cerrar el modal incluso si hay error para mejor UX
        setShowDeleteModal(false);
        setItemToDelete(null);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchSuppliers, pagination.page, pagination.limit, searchTerm],
  );

  // Payment Methods API functions
  const fetchPaymentMethods = useCallback(
    async (page: number = 1, limit: number = 10, search?: string) => {
      try {
        setLoading(true);
        setError(null);
        const response = await PaymentMethodsService.paymentMethodsControllerFindAll(
          page,
          limit,
          search,
        );

        // Handle the response structure with pagination metadata
        if (response.data && Array.isArray(response.data)) {
          setPaymentMethodsData(response.data);

          // Update pagination with server metadata if available
          if (response.meta) {
            setPagination((prev) => ({
              ...prev,
              totalItems: response.meta.total,
              totalPages: response.meta.totalPages,
              page: page,
              limit: response.meta.limit,
            }));

            // Update category totals
            setCategoryTotals((prev) => ({
              ...prev,
              metodosPago: response.meta.total,
            }));
          }
        } else {
          console.warn('Unexpected payment methods response format:', response);
          setPaymentMethodsData([]);
        }
      } catch (err: any) {
        const rawErrorMessage =
          err?.body?.message || err?.message || 'Error al cargar los métodos de pago';
        const errorMessage = translateErrorMessage(rawErrorMessage);
        setError(errorMessage);
        console.error('Error fetching payment methods:', err);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const createPaymentMethod = useCallback(
    async (data: CreatePaymentMethodDto) => {
      try {
        setLoading(true);
        setError(null);
        await PaymentMethodsService.paymentMethodsControllerCreate(data);
        await fetchPaymentMethods(pagination.page, pagination.limit, searchTerm);
      } catch (err: any) {
        const rawErrorMessage =
          err?.body?.message || err?.message || 'Error al crear el método de pago';
        const errorMessage = translateErrorMessage(rawErrorMessage);
        setError(errorMessage);
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        console.error('Error creating payment method:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchPaymentMethods, pagination.page, pagination.limit, searchTerm],
  );

  const updatePaymentMethod = useCallback(
    async (id: number, data: UpdatePaymentMethodDto) => {
      try {
        setLoading(true);
        setError(null);
        await PaymentMethodsService.paymentMethodsControllerUpdate(id.toString(), data);
        await fetchPaymentMethods(pagination.page, pagination.limit, searchTerm);
      } catch (err: any) {
        const rawErrorMessage =
          err?.body?.message || err?.message || 'Error al actualizar el método de pago';
        const errorMessage = translateErrorMessage(rawErrorMessage);
        setError(errorMessage);
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        console.error('Error updating payment method:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchPaymentMethods, pagination.page, pagination.limit, searchTerm],
  );

  const deletePaymentMethod = useCallback(
    async (id: number) => {
      try {
        setLoading(true);
        setError(null);
        await PaymentMethodsService.paymentMethodsControllerRemove(id.toString());
        await fetchPaymentMethods(pagination.page, pagination.limit, searchTerm);
        setShowDeleteModal(false);
        setItemToDelete(null);
      } catch (err: any) {
        const rawErrorMessage =
          err?.body?.message || err?.message || 'Error al eliminar el método de pago';
        const errorMessage = translateErrorMessage(rawErrorMessage);

        setError(errorMessage);
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        console.error('Error deleting payment method:', err);
        setShowDeleteModal(false);
        setItemToDelete(null);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchPaymentMethods, pagination.page, pagination.limit, searchTerm],
  );

  // Load categories, tags, materials, colors, suppliers, and payment methods on mount
  useEffect(() => {
    fetchCategories(1, 10); // Use default values instead of pagination state
    fetchTags(1, 10); // Use default values instead of pagination state
    fetchMaterials(1, 10); // Use default values instead of pagination state
    fetchColors(1, 10); // Use default values instead of pagination state
    fetchSuppliers(1, 10); // Use default values instead of pagination state
    fetchPaymentMethods(1, 10); // Use default values instead of pagination state
  }, [
    fetchCategories,
    fetchTags,
    fetchMaterials,
    fetchColors,
    fetchSuppliers,
    fetchPaymentMethods,
  ]);

  // Pagination metadata for each category
  const [categoryTotals, setCategoryTotals] = useState<{ [key: string]: number }>({
    tipoProducto: 0,
    etiquetas: 0,
    material: 0,
    color: 0,
    proveedor: 0,
    metodosPago: 0,
  });

  // Categories configuration
  const categories: SystemParameterCategories = useMemo(
    () => ({
      tipoProducto: {
        title: 'Tipo de Producto',
        icon: Inventory,
        fields: ['nombre', 'descripcion'],
        data: categoriesData.map((category) => ({
          id: category.id,
          nombre: category.name,
          descripcion: category.description || '',
          codigo: category.code,
          status: category.status,
        })),
        totalCount: categoryTotals.tipoProducto,
      },
      etiquetas: {
        title: 'Etiquetas',
        icon: Label,
        fields: ['nombre', 'color', 'descripcion'],
        data: tagsData.map((tag) => ({
          id: tag.id,
          nombre: tag.name,
          color: tag.color || '#6b7280',
          descripcion: tag.description || '',
        })),
        totalCount: categoryTotals.etiquetas,
      },
      material: {
        title: 'Material',
        icon: Layers,
        fields: ['nombre', 'descripcion'],
        data: materialsData.map((material) => ({
          id: material.id,
          nombre: material.name,
          descripcion: material.description || '',
        })),
        totalCount: categoryTotals.material,
      },
      color: {
        title: 'Color',
        icon: Palette,
        fields: ['nombre', 'descripcion'],
        data: colorsData.map((color) => ({
          id: color.id,
          nombre: color.name,
          descripcion: color.description || '',
        })),
        totalCount: categoryTotals.color,
      },
      proveedor: {
        title: 'Proveedor',
        icon: People,
        fields: ['nombre', 'telefono', 'email', 'contacto', 'descripcion'],
        data: suppliersData.map((supplier) => ({
          id: supplier.id,
          nombre: supplier.name,
          telefono: supplier.phone || '',
          email: supplier.email || '',
          contacto: supplier.contactPerson || '',
          descripcion: supplier.description || '',
        })),
        totalCount: categoryTotals.proveedor,
      },
      metodosPago: {
        title: 'Métodos de Pago',
        icon: CreditCard,
        fields: ['nombre', 'descripcion'],
        data: paymentMethodsData.map((paymentMethod) => ({
          id: paymentMethod.id,
          nombre: paymentMethod.name,
          codigo: paymentMethod.code,
          descripcion: paymentMethod.description || '',
          icono: paymentMethod.icon || '',
          activo: paymentMethod.isActive,
        })),
        totalCount: categoryTotals.metodosPago,
      },
    }),
    [
      categoriesData,
      tagsData,
      materialsData,
      colorsData,
      suppliersData,
      paymentMethodsData,
      categoryTotals,
    ],
  );

  // Current category
  const currentCategory = categories[selectedCategory];

  // Filtered data based on search term
  const filteredData = useMemo(() => {
    if (selectedCategory === 'etiquetas') {
      // For tags, use server-side pagination - data is already paginated
      return tagsData.map((tag) => ({
        id: tag.id,
        nombre: tag.name,
        color: tag.color || '#6b7280',
        descripcion: tag.description || '',
      }));
    } else if (selectedCategory === 'tipoProducto') {
      // For categories, use server-side pagination - data is already paginated
      return categoriesData.map((category) => ({
        id: category.id,
        nombre: category.name,
        descripcion: category.description || '',
        codigo: category.code,
        status: category.status,
      }));
    } else if (selectedCategory === 'material') {
      // For materials, use server-side pagination - data is already paginated
      return materialsData.map((material) => ({
        id: material.id,
        nombre: material.name,
        descripcion: material.description || '',
      }));
    } else if (selectedCategory === 'color') {
      // For colors, use server-side pagination - data is already paginated
      return colorsData.map((color) => ({
        id: color.id,
        nombre: color.name,
        descripcion: color.description || '',
      }));
    } else if (selectedCategory === 'proveedor') {
      // For suppliers, use server-side pagination - data is already paginated
      return suppliersData.map((supplier) => ({
        id: supplier.id,
        nombre: supplier.name,
        telefono: supplier.phone || '',
        email: supplier.email || '',
        contacto: supplier.contactPerson || '',
        descripcion: supplier.description || '',
      }));
    } else if (selectedCategory === 'metodosPago') {
      // For payment methods, use server-side pagination - data is already paginated
      return paymentMethodsData.map((paymentMethod) => ({
        id: paymentMethod.id,
        nombre: paymentMethod.name,
        codigo: paymentMethod.code,
        descripcion: paymentMethod.description || '',
        icono: paymentMethod.icon || '',
        activo: paymentMethod.isActive,
      }));
    } else {
      // For other categories, use client-side pagination (fallback)
      const filtered = currentCategory.data.filter((item) =>
        Object.values(item).some((value) =>
          value.toString().toLowerCase().includes(searchTerm.toLowerCase()),
        ),
      );

      // Update pagination totals for client-side pagination
      const totalItems = filtered.length;
      const totalPages = Math.ceil(totalItems / pagination.limit);

      setPagination((prev) => ({
        ...prev,
        totalItems,
        totalPages,
      }));

      // Apply pagination
      const startIndex = (pagination.page - 1) * pagination.limit;
      const endIndex = startIndex + pagination.limit;

      return filtered.slice(startIndex, endIndex);
    }
  }, [
    currentCategory.data,
    searchTerm,
    pagination.page,
    pagination.limit,
    selectedCategory,
    tagsData,
    categoriesData,
    materialsData,
    colorsData,
    suppliersData,
    paymentMethodsData,
  ]);

  // Handlers
  const handleAdd = useCallback(() => {
    setEditingItem(null);
    setShowAddModal(true);
  }, []);

  const handleEdit = useCallback((item: SystemParameterItem) => {
    setEditingItem(item);
    setShowAddModal(true);
  }, []);

  const handleDelete = useCallback((item: SystemParameterItem) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!itemToDelete) return;

    try {
      if (selectedCategory === 'etiquetas') {
        await deleteTag(itemToDelete.id);
      } else if (selectedCategory === 'tipoProducto') {
        await deleteCategory(itemToDelete.id);
      } else if (selectedCategory === 'material') {
        await deleteMaterial(itemToDelete.id);
      } else if (selectedCategory === 'color') {
        await deleteColor(itemToDelete.id);
      } else if (selectedCategory === 'proveedor') {
        await deleteSupplier(itemToDelete.id);
      } else if (selectedCategory === 'metodosPago') {
        await deletePaymentMethod(itemToDelete.id);
      }
    } catch (err) {
      // Error is already handled in deleteCategory/deleteTag/deleteMaterial/deleteSupplier/deletePaymentMethod
      // El setNotification ya se ejecutó en las funciones de eliminación antes del throw
    }
  }, [
    itemToDelete,
    selectedCategory,
    deleteCategory,
    deleteTag,
    deleteMaterial,
    deleteColor,
    deleteSupplier,
    deletePaymentMethod,
  ]);

  // Función para normalizar el color hexadecimal
  const normalizeHexColor = useCallback((color: string): string => {
    // Si está vacío o es undefined/null, usar color por defecto
    if (!color || color.trim() === '') {
      return '#9CA3AF';
    }

    // Si ya tiene el formato correcto (# seguido de 6 caracteres hex), retornarlo
    if (/^#[0-9A-Fa-f]{6}$/.test(color)) {
      return color.toUpperCase();
    }

    // Si tiene # pero menos de 6 caracteres, completar con ceros
    if (color.startsWith('#')) {
      const hex = color.slice(1);
      if (/^[0-9A-Fa-f]{1,6}$/.test(hex)) {
        return '#' + hex.padEnd(6, '0').toUpperCase();
      }
    }

    // Si no tiene #, agregarlo
    if (/^[0-9A-Fa-f]{6}$/.test(color)) {
      return '#' + color.toUpperCase();
    }

    // Si es un color válido pero incompleto, completar
    if (/^[0-9A-Fa-f]{1,6}$/.test(color)) {
      return '#' + color.padEnd(6, '0').toUpperCase();
    }

    // Si no es válido, retornar el color por defecto
    return '#9CA3AF';
  }, []);

  const handleSave = useCallback(
    async (formData: any) => {
      try {
        if (selectedCategory === 'etiquetas') {
          // Normalizar el color antes de enviarlo
          const normalizedColor = normalizeHexColor(formData.color || '');

          if (editingItem) {
            // Update existing tag
            const updateData: UpdateTagDto = {
              name: formData.nombre,
              color: normalizedColor,
              description: formData.descripcion,
            };
            await updateTag(editingItem.id, updateData);
          } else {
            // Create new tag
            const createData: CreateTagDto = {
              name: formData.nombre,
              color: normalizedColor,
              description: formData.descripcion,
              source: CreateTagDto.source.SETTINGS,
            };
            await createTag(createData);
          }
        } else if (selectedCategory === 'tipoProducto') {
          if (editingItem) {
            // Update existing category
            const updateData: UpdateCategoryDto = {
              name: formData.nombre,
              description: formData.descripcion,
              status: formData.status || UpdateCategoryDto.status.ACTIVE,
            };
            await updateCategory(editingItem.id, updateData);
          } else {
            // Create new category - generate code automatically from name
            const generatedCode =
              formData.nombre
                .toUpperCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '') // Remove accents
                .replace(/[^A-Z0-9]/g, '') // Remove non-alphanumeric
                .substring(0, 20) || 'CATEGORY'; // Limit length and provide fallback
            const createData: CreateCategoryDto = {
              name: formData.nombre,
              code: generatedCode,
              description: formData.descripcion,
              status: CreateCategoryDto.status.ACTIVE,
              source: CreateCategoryDto.source.SETTINGS,
            };
            await createCategory(createData);
          }
        } else if (selectedCategory === 'material') {
          if (editingItem) {
            // Update existing material
            const updateData: UpdateMaterialDto = {
              name: formData.nombre,
              description: formData.descripcion,
            };
            await updateMaterial(editingItem.id, updateData);
          } else {
            // Create new material
            const createData: CreateMaterialDto = {
              name: formData.nombre,
              description: formData.descripcion,
              source: CreateMaterialDto.source.SETTINGS,
            };
            await createMaterial(createData);
          }
        } else if (selectedCategory === 'color') {
          if (editingItem) {
            const updateData: UpdateColorDto = {
              name: formData.nombre,
              description: formData.descripcion,
            };
            await updateColor(editingItem.id, updateData);
          } else {
            const createData: CreateColorDto = {
              name: formData.nombre,
              description: formData.descripcion,
              source: CreateColorDto.source.SETTINGS,
            };
            await createColor(createData);
          }
        } else if (selectedCategory === 'proveedor') {
          if (formData.telefono?.trim()) {
            const { countryIso, nationalNumber } = splitPhoneValue(formData.telefono);
            const phoneError = validatePhoneByCountry(countryIso, nationalNumber);
            if (phoneError) {
              setError(phoneError);
              setNotification({
                open: true,
                message: phoneError,
                severity: 'error',
              });
              return;
            }
          }

          if (editingItem) {
            // Update existing supplier
            const updateData: UpdateSupplierDto = {
              name: formData.nombre,
              phone: formData.telefono?.trim() || undefined,
              email: formData.email?.trim() || undefined,
              contactPerson: formData.contacto?.trim() || undefined,
              description: formData.descripcion?.trim() || undefined,
            };
            await updateSupplier(editingItem.id, updateData);
          } else {
            // Create new supplier
            const createData: CreateSupplierDto = {
              name: formData.nombre,
              phone: formData.telefono?.trim() || undefined,
              email: formData.email?.trim() || undefined,
              contactPerson: formData.contacto?.trim() || undefined,
              description: formData.descripcion?.trim() || undefined,
              status: CreateSupplierDto.status.ACTIVE,
              source: CreateSupplierDto.source.SETTINGS,
            };
            await createSupplier(createData);
          }
        } else if (selectedCategory === 'metodosPago') {
          if (editingItem) {
            // Update: solo nombre/descripción en UI; conservar código, icono y estado existentes
            const prevActivo =
              typeof editingItem.activo === 'boolean'
                ? editingItem.activo
                : editingItem.activo === 'true' || editingItem.activo === true;
            const updateData: UpdatePaymentMethodDto = {
              name: formData.nombre,
              code: editingItem.codigo as string,
              description: formData.descripcion?.trim() || undefined,
              icon: (editingItem.icono as string) || undefined,
              isActive: prevActivo,
            };
            await updatePaymentMethod(editingItem.id, updateData);
          } else {
            const generatedCode =
              formData.nombre
                .toUpperCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/[^A-Z0-9]/g, '')
                .substring(0, 20) || 'PAYMENT_METHOD';
            const createData: CreatePaymentMethodDto = {
              name: formData.nombre,
              code: generatedCode,
              description: formData.descripcion?.trim() || undefined,
              isActive: true,
            };
            await createPaymentMethod(createData);
          }
        }
        setShowAddModal(false);
        setEditingItem(null);
      } catch (err) {
        // Error is already handled in createCategory/updateCategory/createTag/updateTag/createMaterial/updateMaterial/createSupplier/updateSupplier/createPaymentMethod/updatePaymentMethod
        // Cerrar el modal incluso si hay error para mejor UX
        setShowAddModal(false);
        setEditingItem(null);
      }
    },
    [
      editingItem,
      selectedCategory,
      createCategory,
      updateCategory,
      createTag,
      updateTag,
      createMaterial,
      updateMaterial,
      createColor,
      updateColor,
      createSupplier,
      updateSupplier,
      createPaymentMethod,
      updatePaymentMethod,
      normalizeHexColor,
    ],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value;
      setSearchTerm(searchValue);
      setPagination((prev) => ({ ...prev, page: 1 })); // Reset to first page when searching

      // For tags, categories, materials, suppliers, and payment methods, fetch new data from server with search term
      if (selectedCategory === 'etiquetas') {
        fetchTags(1, pagination.limit, searchValue);
      } else if (selectedCategory === 'tipoProducto') {
        fetchCategories(1, pagination.limit, searchValue);
      } else if (selectedCategory === 'material') {
        fetchMaterials(1, pagination.limit, searchValue);
      } else if (selectedCategory === 'color') {
        fetchColors(1, pagination.limit, searchValue);
      } else if (selectedCategory === 'proveedor') {
        fetchSuppliers(1, pagination.limit, searchValue);
      } else if (selectedCategory === 'metodosPago') {
        fetchPaymentMethods(1, pagination.limit, searchValue);
      }
    },
    [
      selectedCategory,
      fetchTags,
      fetchCategories,
      fetchMaterials,
      fetchColors,
      fetchSuppliers,
      fetchPaymentMethods,
      pagination.limit,
    ],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setPagination((prev) => ({ ...prev, page }));

      // For tags, categories, materials, suppliers, and payment methods, fetch new data from server
      if (selectedCategory === 'etiquetas') {
        fetchTags(page, pagination.limit, searchTerm);
      } else if (selectedCategory === 'tipoProducto') {
        fetchCategories(page, pagination.limit, searchTerm);
      } else if (selectedCategory === 'material') {
        fetchMaterials(page, pagination.limit, searchTerm);
      } else if (selectedCategory === 'color') {
        fetchColors(page, pagination.limit, searchTerm);
      } else if (selectedCategory === 'proveedor') {
        fetchSuppliers(page, pagination.limit, searchTerm);
      } else if (selectedCategory === 'metodosPago') {
        fetchPaymentMethods(page, pagination.limit, searchTerm);
      }
    },
    [
      selectedCategory,
      fetchTags,
      fetchCategories,
      fetchMaterials,
      fetchColors,
      fetchSuppliers,
      fetchPaymentMethods,
      pagination.limit,
      searchTerm,
    ],
  );

  const handleItemsPerPageChange = useCallback(
    (limit: number) => {
      setPagination((prev) => ({
        ...prev,
        limit,
        page: 1, // Reset to first page when changing items per page
      }));

      // For tags, categories, materials, suppliers, and payment methods, fetch new data from server
      if (selectedCategory === 'etiquetas') {
        fetchTags(1, limit, searchTerm);
      } else if (selectedCategory === 'tipoProducto') {
        fetchCategories(1, limit, searchTerm);
      } else if (selectedCategory === 'material') {
        fetchMaterials(1, limit, searchTerm);
      } else if (selectedCategory === 'color') {
        fetchColors(1, limit, searchTerm);
      } else if (selectedCategory === 'proveedor') {
        fetchSuppliers(1, limit, searchTerm);
      } else if (selectedCategory === 'metodosPago') {
        fetchPaymentMethods(1, limit, searchTerm);
      }
    },
    [
      selectedCategory,
      fetchTags,
      fetchCategories,
      fetchMaterials,
      fetchColors,
      fetchSuppliers,
      fetchPaymentMethods,
      searchTerm,
    ],
  );

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  // Reset pagination when category changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));

    // Fetch data when switching to etiquetas, tipoProducto, material, color, proveedor, or metodosPago categories
    if (selectedCategory === 'etiquetas') {
      fetchTags(1, pagination.limit, searchTerm);
    } else if (selectedCategory === 'tipoProducto') {
      fetchCategories(1, pagination.limit, searchTerm);
    } else if (selectedCategory === 'material') {
      fetchMaterials(1, pagination.limit, searchTerm);
    } else if (selectedCategory === 'color') {
      fetchColors(1, pagination.limit, searchTerm);
    } else if (selectedCategory === 'proveedor') {
      fetchSuppliers(1, pagination.limit, searchTerm);
    } else if (selectedCategory === 'metodosPago') {
      fetchPaymentMethods(1, pagination.limit, searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]); // Only depend on selectedCategory to avoid unnecessary re-runs

  return {
    // State
    selectedCategory,
    searchTerm,
    showAddModal,
    editingItem,
    loading,
    error,
    showDeleteModal,
    itemToDelete,
    notification,

    // Data
    categories,
    currentCategory,
    filteredData,
    pagination,

    // Handlers
    setSelectedCategory,
    setSearchTerm,
    setShowAddModal,
    setEditingItem,
    setShowDeleteModal,
    setItemToDelete,
    handleAdd,
    handleEdit,
    handleDelete,
    handleConfirmDelete,
    handleSave,
    handleSearchChange,
    handlePageChange,
    handleItemsPerPageChange,
    handleCloseNotification,
    refreshCategories: () => fetchCategories(pagination.page, pagination.limit, searchTerm),
    refreshTags: () => fetchTags(pagination.page, pagination.limit, searchTerm),
    refreshMaterials: () => fetchMaterials(pagination.page, pagination.limit, searchTerm),
    refreshColors: () => fetchColors(pagination.page, pagination.limit, searchTerm),
    refreshSuppliers: () => fetchSuppliers(pagination.page, pagination.limit, searchTerm),
    refreshPaymentMethods: () => fetchPaymentMethods(pagination.page, pagination.limit, searchTerm),
  };
};
